<script setup lang="ts">
import { onMounted } from "vue";
import { useRouter } from "vue-router";
import { setRouterRef } from "@/router/routerRef";
import { useAppStore } from "@/composables/appStore";
import { useAppLayout } from "@/composables/layout";
import { useVersion } from "@/composables/version";
import { Layout } from "@/router/types";
import { ElConfigProvider } from "element-plus";
import elementPlusCs from "@/locales/elementPlusCs";

// Under the Nuxt build, nuxtApp.$router is a separate router instance that never actually
// confirms navigation (this app renders <RouterView> in LayoutDefault.vue, not <NuxtPage>,
// which Nuxt requires to drive that instance — see NUXT_E4011). useRouter() here resolves to
// whatever router actually rendered this component tree, which is the real one.
setRouterRef(useRouter());

const AppLoader = useAsyncComponent(() => import("@/components/generics/AppLoader.vue"));
const NewReleaseBar = useAsyncComponent(() => import("@/components/generics/NewReleaseBar.vue"));
const PopupNotifications = useAsyncComponent(() => import("@/components/popupNotifications/PopupNotifications.vue"));
const CookieConsent = useAsyncComponent(() => import("@/components/generics/CookieConsent.vue"));

// Layout
const { layout } = useAppLayout();
const Layout_Default = useAsyncComponent(() => import("@/components/layouts/LayoutDefault.vue"));
const Layout_LockingState = useAsyncComponent(() => import("@/components/layouts/LayoutLockingState.vue"));

// Latest bundle version check
const version = useVersion();
version.logIntoConsole();
// Registered in onMounted() so the 15-minute interval is never started during a server render.
onMounted(() => version.startAutocheck());
const renderNewReleaseBar = computed(() => version.isLatest.value === false);

// CSS helpers
const isSomeNotificationVisible = computed(() => renderNewReleaseBar.value);
const pageWrapperCssClasses = computed(() => ({
  "page-wrapper": true,
  "with-notification": isSomeNotificationVisible.value,
}));
const appStore = useAppStore();
const renderLayout = computed(() => appStore.isStartupUserCheckDone.value && appStore.languageId.value !== undefined && layout !== undefined);

// Async styles import - we dont want them to be "blocking", however we want them to be loaded everywhere.
// Maybe it's not a bad idea to handle it in a build process via Vite -> no ROI for now
onMounted(async () => await import("floating-vue/dist/style.css"));
// @ts-expect-error CSS-only module, element-plus doesn't ship type declarations for this subpath.
onMounted(async () => await import("element-plus/es/components/loading/style/css"));
</script>

<template>
  <ElConfigProvider :locale="elementPlusCs">
    <NewReleaseBar v-if="renderNewReleaseBar" />

    <transition name="fade-500">
      <div v-if="renderLayout" :class="pageWrapperCssClasses">
        <Layout_Default v-if="layout === Layout.Default" />
        <Layout_LockingState v-else-if="layout === Layout.LockingState" />
      </div>
      <AppLoader v-else />
    </transition>

    <PopupNotifications />
    <CookieConsent />
  </ElConfigProvider>
</template>

<style lang="scss" scoped>
.page-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  @include mixins.maxScreenWidth(md) {
    min-height: 100dvh;
  }

  &.with-notification {
    padding-top: var(--notificationBarHeight);
  }
}
</style>
